import { useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Mail, ChevronLeft, ChevronRight } from "lucide-react"
import { api } from "@/lib/api"
import { formatCurrency, formatDate, LOCALE_MAP } from "@/lib/utils"
import { useAdminAreal } from "@/hooks/useAdminAreal"
import type { BillingCurrentResponse } from "@fohet/caravan-booker-shared"

// "Vyúčtování Kempi" — running estimate of the platform commission Fohet
// will invoice for any calendar month. Historical periods are queried
// live (no closing job yet) so the < / > buttons can walk back through
// time as far as the data goes.

const PartyBlock = ({ title, party }: { title: string; party: BillingCurrentResponse["payer"] }) => {
    const { t } = useTranslation()
    return (
        <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-2">{title}</div>
            <div className="text-slate-900 font-black">{party.companyName}</div>
            <div className="text-slate-600 font-bold text-sm mt-1">{party.addressStreet}</div>
            <div className="text-slate-600 font-bold text-sm">
                {[party.addressZip, party.addressCity].filter(Boolean).join(" ")}
                {party.addressCountry ? `, ${party.addressCountry}` : ""}
            </div>
            {party.companyId && (
                <div className="text-slate-600 font-bold text-sm mt-2">
                    {t("admin.billing.companyId")}: {party.companyId}
                </div>
            )}
            {party.vatId && (
                <div className="text-slate-600 font-bold text-sm">
                    {t("admin.billing.vatId")}: {party.vatId}
                </div>
            )}
        </div>
    )
}

const toYearMonth = (d: Date): string => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    return `${y}-${m}`
}

const shiftMonth = (yearMonth: string, delta: number): string => {
    const [y, m] = yearMonth.split("-").map(Number)
    if (!y || !m) return yearMonth
    const d = new Date(Date.UTC(y, m - 1 + delta, 1))
    return toYearMonth(d)
}

const formatMonthLabel = (yearMonth: string, locale: string): string => {
    const [y, m] = yearMonth.split("-").map(Number)
    if (!y || !m) return yearMonth
    return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(new Date(y, m - 1, 1))
}

export const AdminBillingPage = () => {
    const { t, i18n } = useTranslation()
    const { data: areal } = useAdminAreal()
    const arealId = areal?.id
    const locale = LOCALE_MAP[i18n.language] ?? "en-US"

    const currentYearMonth = useMemo(() => toYearMonth(new Date()), [])
    const [yearMonth, setYearMonth] = useState(currentYearMonth)
    const isCurrent = yearMonth === currentYearMonth

    const { data, isLoading, error } = useQuery({
        queryKey: ["admin", "billing", arealId, yearMonth],
        queryFn: () => api.get<BillingCurrentResponse>(`/areals/${arealId}/billing/current?month=${yearMonth}`),
        enabled: !!arealId,
    })

    if (!areal) {
        return <div className="text-slate-500 font-bold">{t("admin.billing.selectAreal")}</div>
    }

    const periodLabel = data
        ? `${formatDate(data.periodStart, locale)} – ${formatDate(data.periodEnd, locale)}`
        : ""
    const commissionPctLabel = data
        ? `${Number(data.commissionPct).toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`
        : ""

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-slate-900">
                        {t("admin.billing.title")}
                    </h1>
                    <p className="text-slate-500 font-bold mt-1">
                        {isCurrent ? t("admin.billing.subtitleCurrent", { period: periodLabel }) : t("admin.billing.subtitlePast", { period: periodLabel })}
                    </p>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-2xl border border-slate-200 px-3 py-2 shadow-sm">
                    <button
                        type="button"
                        onClick={() => setYearMonth(shiftMonth(yearMonth, -1))}
                        className="p-1 rounded-lg hover:bg-slate-100 text-slate-600"
                        aria-label={t("admin.billing.prevMonth")}
                    >
                        <ChevronLeft className="size-4" />
                    </button>
                    <div className="font-black text-slate-800 min-w-[140px] text-center capitalize">
                        {formatMonthLabel(yearMonth, locale)}
                    </div>
                    <button
                        type="button"
                        onClick={() => setYearMonth(shiftMonth(yearMonth, 1))}
                        disabled={isCurrent}
                        className="p-1 rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label={t("admin.billing.nextMonth")}
                    >
                        <ChevronRight className="size-4" />
                    </button>
                </div>
            </header>

            {isLoading && <div className="text-slate-500 font-bold">{t("admin.billing.loading")}</div>}
            {error && <div className="text-red-600 font-bold">{t("admin.billing.loadError")}</div>}

            {data && (
                <>
                    <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                        <PartyBlock title={t("admin.billing.payer")} party={data.payer} />
                        <PartyBlock title={t("admin.billing.payee")} party={data.payee} />
                    </div>

                    <div className="bg-charcoal text-white rounded-[40px] shadow-xl p-8">
                        <div className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">
                            {isCurrent ? t("admin.billing.summary") : t("admin.billing.summaryPast")}
                        </div>
                        <dl className="space-y-2">
                            <div className="flex items-center justify-between">
                                <dt className="text-slate-300 font-bold">{t("admin.billing.gross")}</dt>
                                <dd className="font-black text-lg">{formatCurrency(data.totalGross, data.currency, locale)}</dd>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <dt className="text-slate-400 font-bold">{t("admin.billing.rate")}</dt>
                                <dd className="text-slate-200 font-bold">{commissionPctLabel}</dd>
                            </div>
                            {data.payeeIsVatPayer && (
                                <>
                                    <div className="border-t border-white/10 my-3" />
                                    <div className="flex items-center justify-between text-sm">
                                        <dt className="text-slate-300 font-bold">{t("admin.billing.vatBase")}</dt>
                                        <dd className="font-bold">{formatCurrency(data.totalGross, data.currency, locale)}</dd>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <dt className="text-slate-300 font-bold">{t("admin.billing.vat", { pct: data.vatPct })}</dt>
                                        <dd className="font-bold">{formatCurrency(data.totalVat, data.currency, locale)}</dd>
                                    </div>
                                    <div className="border-t border-white/10 my-3" />
                                    <div className="flex items-center justify-between">
                                        <dt className="text-white font-black">{t("admin.billing.totalWithVat")}</dt>
                                        <dd className="font-black text-2xl text-brand">
                                            {formatCurrency(data.totalWithVat, data.currency, locale)}
                                        </dd>
                                    </div>
                                </>
                            )}
                            {!data.payeeIsVatPayer && (
                                <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                                    <dt className="text-white font-black">{t("admin.billing.total")}</dt>
                                    <dd className="font-black text-2xl text-brand">
                                        {formatCurrency(data.totalGross, data.currency, locale)}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>

                    {isCurrent && (
                        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start gap-3">
                            <Mail className="size-5 text-blue-600 shrink-0 mt-0.5" />
                            <div className="text-sm font-bold text-blue-900">{t("admin.billing.invoiceByMail")}</div>
                        </div>
                    )}

                    <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-8">
                        <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-4">
                            {t("admin.billing.includedReservations")} ({data.lines.length})
                        </div>
                        {data.lines.length === 0 ? (
                            <div className="text-slate-500 font-bold text-sm">{t("admin.billing.empty")}</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-slate-200">
                                            <th className="text-left font-black uppercase tracking-widest text-xs text-slate-500 py-2 pr-4">
                                                {t("admin.billing.colVs")}
                                            </th>
                                            <th className="text-left font-black uppercase tracking-widest text-xs text-slate-500 py-2 pr-4">
                                                {t("admin.billing.colGuest")}
                                            </th>
                                            <th className="text-left font-black uppercase tracking-widest text-xs text-slate-500 py-2 pr-4">
                                                {t("admin.billing.colPaidAt")}
                                            </th>
                                            <th className="text-right font-black uppercase tracking-widest text-xs text-slate-500 py-2 pr-4">
                                                {t("admin.billing.colAmount")}
                                            </th>
                                            <th className="text-right font-black uppercase tracking-widest text-xs text-slate-500 py-2 pr-4">
                                                {t("admin.billing.colBase")}
                                            </th>
                                            <th className="text-right font-black uppercase tracking-widest text-xs text-slate-500 py-2">
                                                {t("admin.billing.colCommission")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.lines.map((line) => (
                                            <tr key={line.reservationId} className="border-b border-slate-50">
                                                <td className="py-2 pr-4 font-bold text-slate-700">{line.variableSymbol ?? "—"}</td>
                                                <td className="py-2 pr-4 font-bold text-slate-700">{line.guestName || "—"}</td>
                                                <td className="py-2 pr-4 font-bold text-slate-500">{formatDate(line.paidAt, locale)}</td>
                                                <td className="py-2 pr-4 font-bold text-slate-700 text-right">
                                                    {formatCurrency(line.totalAmount, data.currency, locale)}
                                                </td>
                                                <td className="py-2 pr-4 font-bold text-slate-600 text-right">
                                                    {formatCurrency(line.commissionBase, data.currency, locale)}
                                                </td>
                                                <td className="py-2 font-black text-slate-900 text-right">
                                                    {formatCurrency(line.commission, data.currency, locale)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
